import punycode from 'punycode';
import { getDomain, getHostname, getPublicSuffix } from 'tldts';

/**
 * Unicode → Punycode
 */
export function toPunycode(host: string): string {
  return punycode.toASCII(host.trim());
}

/**
 * Punycode → Unicode
 */
export function toUnicode(input: string): string {
  try {
    // Try to parse as a full URL (with protocol like http/https)
    let url: URL;
    try {
      url = new URL(input);
    } catch {
      // If parsing fails, assume it's just a hostname
      return punycode.toUnicode(input.trim());
    }

    // Decode the hostname (punycode → unicode)
    const unicodeHost = punycode.toUnicode(url.hostname);

    // Rebuild the URL with decoded hostname
    return `${url.protocol}//${unicodeHost}${url.port ? `:${url.port}` : ''}${url.pathname}${
      url.search
    }${url.hash}`;
  } catch {
    return input; // fallback: return as-is if something goes wrong
  }
}

/**
 * Domain name validation
 */
export function isValidDomain(host: string): boolean {
  try {
    // Reject empty, leading/trailing dots, and double dots
    if (!host || host.startsWith('.') || host.endsWith('.') || host.includes('..')) {
      return false;
    }

    const ascii = toPunycode(host);

    const hostname = getHostname(ascii);
    if (!hostname) return false;

    const domain = getDomain(ascii);
    const suffix = getPublicSuffix(ascii);
    if (!domain || !suffix) return false;

    // Overall hostname length
    if (ascii.length > 253) return false;

    const labels = ascii.split('.');

    // Each label 1–63 characters
    if (labels.some((l) => l.length < 1 || l.length > 63)) return false;

    // TLD must consist only of letters a–z
    const tld = labels[labels.length - 1];
    if (!/^[a-z]{2,63}$/.test(tld)) return false;

    return true;
  } catch {
    return false;
  }
}
